import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getShipmentFacets } from "../service";
import type { CptSlabLevelFacet, ShipmentModeFacet } from "../types";
import { QueryKeys } from "../../R2DOrders/hooks/types";

export const useShipmentFacets = (
  statusFilter: "undelivered" | "delivered" | "cancelled",
  isApiConfigReady: boolean = false
) => {
  const { data, isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: [QueryKeys.SHIPMENT_FACETS, statusFilter],
    queryFn: () => getShipmentFacets(statusFilter),
    enabled: isApiConfigReady,
    staleTime: 0, 
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000,
  });

  const cptSlabLevels = useMemo(() => {
    return (
      data?.facets?.cptSlabLevels?.map((item) =>
        typeof item === "number" ? item : item.level
      ) || []
    );
  }, [data?.facets?.cptSlabLevels]);

  const cptSlabLevelFacets = useMemo<CptSlabLevelFacet[]>(() => {
    return (
      data?.facets?.cptSlabLevels?.map((item) =>
        typeof item === "number"
          ? { level: item, count: 0 }
          : { level: item.level, count: item.count }
      ) || []
    );
  }, [data?.facets?.cptSlabLevels]);

  const shipmentModes = useMemo<ShipmentModeFacet[]>(() => {
    return (
      data?.facets?.dispatchMode?.map((item) => ({
        mode: item.mode,
        count: item.count,
      })) || []
    );
  }, [data?.facets?.dispatchMode]);

  return {
    facets: data?.facets,
    cptSlabLevels,
    cptSlabLevelFacets,
    shipmentModes,
    loading: isLoading,
    refetch,
    dataUpdatedAt,
  };
};
